import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line no-unused-vars (useRef hâlâ başka yerlerde kullanılıyor)
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useNavigate, useLocation } from 'react-router';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input, { Textarea } from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import Tabs from '../../components/ui/Tabs';
import { Icon } from '../../components/ui/Icon';
import ActionIconButton from '../../components/ui/ActionIconButton';
import DeleteModal from '../../components/ui/DeleteModal';

import {
  fetchPeriods,
  createPeriod,
  clonePeriod,
  updatePeriod,
  activatePeriod,
  closePeriod,
  deletePeriod,
} from '../../store/slices/periodsSlice';
import {
  fetchSegments,
  fetchMudurlukler,
} from '../../store/slices/referencesSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { formatDate, formatASPara } from '../../utils/format';
import { periodSchema, validate, firstError } from '../../utils/validation';
import { badgesApi } from '../../services/api';
import apiClient from '../../services/apiClient';
import BadgeUploadModal from '../../components/ui/BadgeUploadModal';
import useJobWatcher from '../../hooks/useJobWatcher';
import PeriodDetail from './PeriodDetail';
import EvaluateDialog from './EvaluateDialog';

const STATUS_COLORS = {
  active:      { color: 'green',  label: 'Aktif'       },
  closed:      { color: 'neutral', label: 'Kapalı'     },
  draft:       { color: 'yellow', label: 'Taslak'      },
  evaluating:  { color: 'blue',   label: 'Hesaplanıyor' },
};

/**
 * Yeni dönem formu ilk açıldığında içinde bulunulan ayı otomatik doldurur —
 * kullanıcı isterse ad/tarih alanlarını değiştirebilir, ama tipik kullanım
 * "içinde bulunulan ay için dönem aç" olduğundan defaults elle yazım yükünü
 * ortadan kaldırıyor.
 */
const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

function monthDefaults(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-index
  const pad = (n) => String(n).padStart(2, '0');
  const firstDay = `${y}-${pad(m + 1)}-01`;
  // new Date(y, m+1, 0) → sonraki ayın 0. günü = bu ayın son günü
  const last = new Date(y, m + 1, 0).getDate();
  const lastDay = `${y}-${pad(m + 1)}-${pad(last)}`;
  return {
    name: `${TR_MONTHS[m]} ${y}`,
    start_date: firstDay,
    end_date: lastDay,
  };
}

function PeriodsList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, status, pagination } = useSelector((s) => s.periods);
  const { segments, segmentsStatus } = useSelector((s) => s.references);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [showForm, setShowForm] = useState(false);
  // DRAFT bir dönem için aynı PeriodForm'u "edit" modunda açmak — backend
  // `update_period` yalnızca DRAFT statüsünde izin veriyor.
  const [editTarget, setEditTarget] = useState(null);
  const [evaluateTarget, setEvaluateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);

  // Segment listesi yoksa yükle — tabloda segment adlarını göstermek için
  useEffect(() => {
    if (segmentsStatus === 'idle') dispatch(fetchSegments({ include_inactive: false }));
  }, [dispatch, segmentsStatus]);

  // PeriodDetail'deki "Düzenle" butonu buraya navigate ederek editId geçiriyor.
  // items yüklendikten sonra ilgili dönem bulunup editTarget'e set edilir.
  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId || !items.length) return;
    const found = items.find((p) => String(p.id) === String(editId));
    if (found) {
      setEditTarget(found);
      // State'i temizle — geri gelince tekrar açılmasın.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.editId, items]);

  // Filtre değişince ilk sayfaya dön — aksi halde boş sayfa gösterebiliriz.
  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  useEffect(() => {
    const params = { page, size };
    if (filterStatus) params.status = filterStatus;
    dispatch(fetchPeriods(params));
  }, [dispatch, page, size, filterStatus]);

  // Hesaplama worker'da arka planda çalışır; iş bitince listeyi o anki
  // sayfa/filtreyle yenile (durum ve son hesaplama bilgisi yenilemeden görünsün).
  const watchJob = useJobWatcher();
  const reloadPeriodsRef = useRef(null);
  reloadPeriodsRef.current = () => {
    const params = { page, size };
    if (filterStatus) params.status = filterStatus;
    dispatch(fetchPeriods(params));
  };

  // Backend zaten filtreli liste döndürüyor; mock katmanında da filtre uygulanmış.
  const filtered = items;

  const columns = [
    {
      key: 'name',
      header: 'Dönem',
      render: (r) => (
        <div>
          <p className="font-medium text-neutral-900">{r.name}</p>
          {r.description && <p className="text-xs text-neutral-500">{r.description}</p>}
        </div>
      ),
    },
    {
      key: 'dates',
      header: 'Tarih Aralığı',
      render: (r) => (
        <span className="text-xs text-neutral-600">
          {formatDate(r.start_date)} — {formatDate(r.end_date)}
        </span>
      ),
    },
    {
      key: 'segment_ids',
      header: 'Kullanıcı Tipi (Segment)',
      render: (r) => {
        const ids = Array.isArray(r.segment_ids) ? r.segment_ids : [];
        if (!ids.length) return <span className="text-xs text-neutral-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {ids.map((id) => {
              const seg = segments.find(
                (s) => s.name === id || String(s.id) === String(id),
              );
              return (
                <Badge key={id} color="blue" size="sm">
                  {seg ? seg.name : id}
                </Badge>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'task_count',
      header: 'Görev',
      render: (r) => <span className="text-sm font-medium">{r.task_count}</span>,
    },
    {
      key: 'points',
      header: 'Puan (Min / Max)',
      render: (r) => {
        // Backend `baraj_puan`'ı yalnızca `reward_template` JSONB içinde
        // tutar (PeriodOut top-level alanı yok). Eski kayıtlarla geriye
        // uyumluluk için iki kaynağı da kontrol ediyoruz.
        const minRaw = r.baraj_puan ?? r.reward_template?.baraj_puan;
        const maxRaw = r.max_total_points;
        // 0 ya da null → "değer set edilmedi" — yeni Boş Başla dönemde
        // pydantic baraj_puan default'u 0 geldiği için 0'ı da "set edilmedi"
        // olarak yorumluyoruz. Admin gerçekten 0 baraj istiyorsa Düzenle
        // ekranında 0 girmiş olmasının davranışsal bir farkı yok (worker
        // 0 baraj = herkes geçer mantığı zaten).
        const minSet = minRaw != null && Number(minRaw) > 0;
        const maxSet = maxRaw != null && Number(maxRaw) > 0;
        if (!minSet && !maxSet) {
          return (
            <span className="text-xs text-neutral-400" title="Bu dönem için baraj/üst sınır tanımlanmamış. Düzenle ekranından ekleyebilirsiniz.">
              Sınırsız
            </span>
          );
        }
        return (
          <span className="text-xs text-neutral-700 font-medium tabular-nums">
            {minSet ? Number(minRaw) : '0'}
            {' / '}
            {maxSet ? Number(maxRaw) : '∞'}
          </span>
        );
      },
    },
    // "Son Dry-Run" kolonu 2026-05-14'te kaldırıldı. Veri çoğunlukla boş
    // ("Henüz hesaplanmadı") görünüyor, kullanıcıya bilgi katkısı düşük;
    // dry-run sonuçları Dönem Detay sayfasındaki DryRunPanel'de zaten görünür.
    // Kaldırıldı: dryRunMetas / dryRunPopover popover'ı, mini top-5 önizleme.
    {
      key: 'status',
      header: 'Durum',
      render: (r) => {
        const s = STATUS_COLORS[(r.status || '').toLowerCase()] || { color: 'gray', label: r.status || '?' };
        return <Badge color={s.color}>{s.label}</Badge>;
      },
    },
    {
      // email_app GridRowActions ile aynı pattern — her aksiyon küçük bir
      // ikon butonu; ikon rengi aksiyonun semantiğini taşır:
      //   mavi  → detay/görüntüle     (primary)
      //   yeşil → aktifleştir/onayla  (#22C55E)
      //   mor   → hesapla/dry-run     (#8B5CF6)
      //   pembe → kapat/sil            (#FD5995)
      key: 'actions',
      header: 'İşlemler',
      className: 'text-right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <ActionIconButton
            icon="eye"
            iconColor="#0085FF"
            title="Detayları gör"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/periods/${r.id}`);
            }}
          />
          {/* Düzenle — tüm statülerde erişilebilir */}
          <ActionIconButton
            icon="edit"
            iconColor="#3CBFFF"
            title="Düzenle"
            onClick={(e) => {
              e.stopPropagation();
              setEditTarget(r);
            }}
          />
          {(r.status || '').toLowerCase() === 'draft' && (
            <>
              <ActionIconButton
                icon="play"
                iconColor="#22C55E"
                title="Aktifleştir"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(activatePeriod(r.id)).then((res) => {
                    if (res.meta?.requestStatus === 'fulfilled') {
                      dispatch(pushToast({ kind: 'success', message: `"${r.name}" aktifleştirildi.` }));
                      const params = { page, size };
                      if (filterStatus) params.status = filterStatus;
                      dispatch(fetchPeriods(params));
                    } else {
                      dispatch(pushToast({ kind: 'error', message: res.payload || 'Aktifleştirilemedi.' }));
                    }
                  });
                }}
              />
              <ActionIconButton
                icon="trash"
                iconColor="#EF4444"
                title="Sil"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(r);
                }}
              />
            </>
          )}
          {['active', 'evaluating'].includes((r.status || '').toLowerCase()) && (
            <>
              {(r.status || '').toLowerCase() === 'active' && (
                <ActionIconButton
                  icon="chart"
                  iconColor="#8B5CF6"
                  title="Hesapla"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEvaluateTarget(r);
                  }}
                />
              )}
              <ActionIconButton
                icon="stop"
                iconColor="#FD5995"
                title="Dönemi kapat"
                onClick={(e) => {
                  e.stopPropagation();
                  setCloseTarget(r);
                }}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dönem Yönetimi"
        subtitle="Gamification dönemlerini oluştur, aktifleştir ve kapat"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
        actions={
          <Button icon="plus" onClick={() => setShowForm(true)}>
            Yeni Dönem
          </Button>
        }
      />

      <DönemKapatmaInfoBox />

      {/*
        zuzzuu_customers GridTable kabuğuyla hizalı tek kart: filtre toolbar
        + tablo + pagination aynı Card içinde. Customers App Box
        (`rounded-[1.25rem] border-[#E1E1E1] bg-white padding:1rem`) ile
        birebir aynı tokenlar.
      */}
      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            placeholder="Tüm durumlar"
            options={[
              { value: 'active', label: 'Aktif' },
              { value: 'draft', label: 'Taslak' },
              { value: 'closed', label: 'Kapalı' },
            ]}
            className="w-auto min-w-[12rem]"
          />
        </div>

        <Table
          columns={columns}
          rows={filtered}
          loading={status === 'loading'}
          onRowClick={(r) => navigate(`/periods/${r.id}`)}
          emptyMessage={
            status === 'failed'
              ? 'Dönem listesi alınamadı. Sunucuya ulaşılamıyor — servis yayına alındıktan sonra yeniden deneyin.'
              : 'Dönem bulunamadı'
          }
        />

        <div className="px-4 pb-4 pt-2">
          <Pagination
            page={page}
            size={size}
            total={pagination?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
            loading={status === 'loading'}
          />
        </div>
      </Card>

      <PeriodForm
        open={showForm || !!editTarget}
        editing={editTarget}
        onClose={() => {
          setShowForm(false);
          setEditTarget(null);
        }}
      />
      <EvaluateDialog
        period={evaluateTarget}
        onClose={() => setEvaluateTarget(null)}
        onJobQueued={({ jobId, dryRun }) =>
          watchJob(jobId, {
            label: `"${evaluateTarget?.name || 'Dönem'}" ${dryRun ? 'deneme hesabı' : 'hesaplaması'}`,
            onDone: () => reloadPeriodsRef.current?.(),
          })
        }
      />

      <DeleteModal
        open={!!deleteTarget}
        title="Dönemi silmek istediğinize emin misiniz?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" dönemi ve bağlı tüm görevler kalıcı olarak silinecek. Bu işlem geri alınamaz.`
            : ''
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          dispatch(deletePeriod(deleteTarget.id)).then(() => {
            dispatch(pushToast({ kind: 'success', message: 'Dönem silindi.' }));
          });
          setDeleteTarget(null);
        }}
      />

      {/* Dönem Kapatma Onay Dialogu */}
      {closeTarget && (
        <Modal
          open
          title="Dönemi kapat"
          onClose={() => setCloseTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCloseTarget(null)}>
                Vazgeç
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  dispatch(closePeriod(closeTarget.id)).then(() => {
                    dispatch(pushToast({ kind: 'success', message: `"${closeTarget.name}" dönemi kapatıldı.` }));
                  });
                  setCloseTarget(null);
                }}
              >
                Evet, Dönemi Kapat
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-neutral-700">
              <strong>"{closeTarget.name}"</strong> dönemi kapatılacak. Skorlar dondurulur; gerekirse dönem bitiş tarihi geçmeden yeniden açılabilir.
            </p>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
              <div className="flex items-start gap-2">
                <Icon name="alert-triangle" className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-amber-800">Kapatmadan önce kontrol edin</p>
              </div>
              <ul className="text-sm text-amber-900 space-y-1 pl-6 list-disc">
                <li>
                  Veriler <strong>T-1 modeli</strong> ile aktarılır — dönemin son günü (
                  {closeTarget.end_date ? new Date(closeTarget.end_date).toLocaleDateString('tr-TR') : '—'})
                  verisi ancak bir sonraki gün gelir.
                </li>
                <li>
                  En güvenli kapatma zamanı: dönem bitiş tarihinden <strong>en az 1 gün sonra</strong>, günlük cron'un tamamlandığı teyit edildikten sonra.
                </li>
                <li>
                  Erken kapatırsanız son günün verileri hesaba <strong>girmez</strong>.
                </li>
              </ul>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


function DönemKapatmaInfoBox() {
  const [open, setOpen] = useState(false);

  // Dinamik ay örneği: mevcut ay ve bir sonraki ay
  const TR_MONTHS = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ];
  const now = new Date();
  const curMonthIdx = now.getMonth();                         // 0-based
  const nextMonthIdx = (curMonthIdx + 1) % 12;
  const curMonth = TR_MONTHS[curMonthIdx];                    // ör. "Mayıs"
  const nextMonth = TR_MONTHS[nextMonthIdx];                  // ör. "Haziran"
  // Son gün: mevcut ayın son günü
  const lastDay = new Date(now.getFullYear(), curMonthIdx + 1, 0).getDate(); // ör. 31

  return (
    <div className="rounded-[1.25rem] border border-[#E1E1E1] bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-amber-50/60 hover:bg-amber-100/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Icon name="alert-triangle" className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800">Dönem kapatma — T-1 veri akışı ve güvenli kapatma zamanı</span>
        </div>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} className="h-4 w-4 text-amber-500 flex-shrink-0" />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 space-y-3 text-sm text-amber-950 bg-amber-50/40">
          <p>
            Anadolu Sigorta verileri <strong>T-1 modeli</strong> ile aktarılır: her gün bir önceki günün işlemleri gelir.
            Örneğin {lastDay} {curMonth} verisi ancak <strong>1 {nextMonth} sabahında</strong> sisteme yüklenir.
          </p>
          <div className="rounded-lg bg-amber-100/60 border border-amber-200 px-3 py-2 space-y-1.5">
            <p className="text-xs font-semibold text-amber-900">Güvenli kapatma adımları ({curMonth} dönemi örneği):</p>
            <ol className="text-xs text-amber-800 space-y-1 pl-4 list-decimal">
              <li>{lastDay} {curMonth} — dönem bitiyor, henüz kapatmayın.</li>
              <li>1 {nextMonth} sabahı — T-1 batch çalışır, {lastDay} {curMonth} verisi sisteme yüklenir.</li>
              <li>1 {nextMonth} — günlük cron (evaluate-all) tamamlandığını teyit edin.</li>
              <li>1 {nextMonth} (batch sonrası) — dönemi güvenle kapatabilirsiniz. ✅</li>
            </ol>
          </div>
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
            <p className="text-xs font-semibold text-red-800">❌ Yapmamanız gereken:</p>
            <p className="text-xs text-red-700 mt-0.5">
              {lastDay} {curMonth} akşamı dönemi kapatmak — 1 {nextMonth}'ta gelen son günün T-1 verisi hesaba girmez, skorlar eksik kalır.
            </p>
          </div>
          <p className="text-xs text-amber-700">
            Haftalık joker görevler ve RANK tipli görevler de aynı kurala tabidir — görev bitiş tarihinin ertesi günü cron çalıştıktan sonra puanlar yazılır.
          </p>
        </div>
      )}
    </div>
  );
}


/**
 * Bir dönem kaydını form state'ine indirgiyoruz. Backend
 * ``reward_template`` içinde ``tiers`` ve ``baraj_puan`` tutuyor; form
 * tarafında bunları düz alanlar (``reward_tiers``, ``min_total_points``)
 * olarak tutmak ve submit'te tekrar ``reward_template`` altında toparlamak
 * editing ve create akışlarını ortak buildPayload ile besleyebilmek için
 * gerekli.
 */
function periodToFormState(p) {
  const rt = p?.reward_template || {};
  const mapTiers = (arr) =>
    Array.isArray(arr)
      ? arr.map((t) => ({
          rank_from: Number(t.rank_from ?? 0),
          rank_to: Number(t.rank_to ?? t.rank_from ?? 0),
          aspara: Number(t.aspara ?? 0),
        }))
      : [];
  // v2 format: bolge_tiers + genel_tiers ayrı.
  // v1 geriye-uyum: scope + tiers → ilgili alana dönüştür.
  const _hasV2 = rt.bolge_tiers !== undefined || rt.genel_tiers !== undefined;
  const _legacyScope = (rt.scope || 'bolge').toLowerCase();
  const bolgeTiers = mapTiers(
    _hasV2 ? rt.bolge_tiers : (_legacyScope === 'bolge' ? rt.tiers : [])
  );
  const genelTiers = mapTiers(
    _hasV2 ? rt.genel_tiers : (_legacyScope === 'genel' ? rt.tiers : [])
  );
  // per_mudurluk: { "KIBRIS ...": [tier,...], ... } → UI icin
  // [{mudurluk, tiers}] dizisine donustur (daha kolay render).
  const perMudurlukEntries = rt.per_mudurluk
    ? Object.entries(rt.per_mudurluk).map(([mudurluk, arr]) => ({
        mudurluk,
        tiers: mapTiers(arr),
      }))
    : [];
  // Yönetici kademeleri — HYO sıralamasına göre aspara ödülleri.
  const managerTiers = mapTiers(rt.manager_tiers);
  // Rozet ödülleri — bölge içi / türkiye geneli / yönetici için ayrı ayrı.
  const _mapBadgeArr = (arr) =>
    Array.isArray(arr)
      ? arr.map((a) => ({ badge_id: Number(a.badge_id), rank_from: Number(a.rank_from ?? 1), rank_to: Number(a.rank_to ?? 1) }))
      : [];
  const _ba = rt.badge_awards;
  // Geriye dönük uyum: eski flat list ise hepsini bolge'ye al.
  const badgeAwardsBolge   = Array.isArray(_ba) ? _mapBadgeArr(_ba) : _mapBadgeArr(_ba?.bolge);
  const badgeAwardsGenel   = _mapBadgeArr((_ba && !Array.isArray(_ba)) ? _ba.genel   : []);
  const badgeAwardsManager = _mapBadgeArr((_ba && !Array.isArray(_ba)) ? _ba.manager : []);
  return {
    name: p?.name ?? '',
    start_date: p?.start_date ?? '',
    end_date: p?.end_date ?? '',
    min_total_points: Number(rt.baraj_puan ?? 0),
    max_total_points: p?.max_total_points ?? '',
    aspara_per_point: Number(p?.aspara_per_point ?? 0),
    description: p?.description ?? '',
    segment_ids: Array.isArray(p?.segment_ids) ? p.segment_ids.map(String) : [],
    mudurluk_kodlari: Array.isArray(p?.mudurluk_kodlari) ? p.mudurluk_kodlari : [],
    // Bölge içi ve Türkiye geneli sıralama ödülleri — bağımsız, ikisi birden aktif olabilir.
    reward_tiers: bolgeTiers,        // bölge içi (rank_bolge)
    genel_tiers: genelTiers,         // Türkiye geneli (rank)
    reward_per_mudurluk: perMudurlukEntries,
    // Yönetici oyunlaştırma: HYO sıralaması + baraj.
    manager_tiers: managerTiers,
    manager_baraj_min_sks: Number(rt.manager_baraj_min_sks ?? 1),
    manager_baraj_min_hyo: Number(rt.manager_baraj_min_hyo ?? 0),
    // Dönem rozet ödülleri — bölge içi / türkiye geneli / yönetici.
    badge_awards_bolge:   badgeAwardsBolge,
    badge_awards_genel:   badgeAwardsGenel,
    badge_awards_manager: badgeAwardsManager,
  };
}

function PeriodForm({ open, onClose, editing }) {
  const dispatch = useDispatch();
  const {
    segments,
    segmentsStatus,
    mudurlukler,
    mudurluklerStatus,
  } = useSelector((s) => s.references);
  // "Baslangic Sablonu" icin kaynak donem listesi. Yalnizca kendisi
  // (editing) haric, DRAFT olmayan kaynaklar (evaluated + closed + active
  // dahil) kopyalanabilir. Bos liste durumunda clone secenegi sunulmaz.
  const allPeriods = useSelector((s) => s.periods.items);
  const periodOptionsForClone = (allPeriods || [])
    .filter((p) => !editing || p.id !== editing.id)
    .map((p) => ({ id: p.id, name: p.name }));
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    // Ay bazlı defaults — içinde bulunulan ay "Nisan 2026" / 2026-04-01 /
    // 2026-04-30 olarak auto-fill. Değişmek istenirse alan elle düzenlenir.
    ...monthDefaults(),
    // "Boş Başla" akışı için baraj puanı + üst sınır boş başlasın —
    // kullanıcı kademe planına göre rakam girer. Hardcoded 80 default
    // test ortamından kalmıştı; prod'da müşteri her dönem için baraj
    // puanını sıfırdan belirliyor.
    min_total_points: '',
    max_total_points: '',
    // Puan → ASPara dönüşüm oranı (1 puan = 0,5 ASPara için 0.5). 0 ⇒ devre dışı.
    aspara_per_point: 0,
    description: '',
    // Hedef kitle — CH `segments` tablosundan dropdown'a doldurulur (tenant
    // JWE token ile filtrelenir). UI'da "Kullanıcı Tipi (Segment)" etiketiyle gösterilir.
    segment_ids: [],
    mudurluk_kodlari: [],
    // Rank bazlı ödül kademeleri — her satır
    //   { rank_from, rank_to, aspara }
    // şeklinde backend `reward_template.tiers`'e gider. Etiket (label) alanı
    // UI'da sunulmuyor; buildPayload'da rank aralığından otomatik türetiliyor.
    // Boş liste ⇒ sadece `aspara_per_point` üzerinden puan bazlı dönüşüm.
    // Bölge içi sıralama ödülleri (rank_bolge'a göre, per_mudurluk override ile)
    reward_tiers: [],
    reward_per_mudurluk: [], // [{mudurluk, tiers: [...]}]
    // Türkiye geneli sıralama ödülleri (genel rank'a göre) — bağımsız
    genel_tiers: [],
    // Yönetici oyunlaştırma.
    manager_tiers: [],
    manager_baraj_min_sks: 1,
    manager_baraj_min_hyo: 0,
    // Dönem rozet ödülleri — bölge içi / türkiye geneli / yönetici.
    badge_awards_bolge: [],
    badge_awards_genel: [],
    badge_awards_manager: [],
    // "Baslangic Sablonu" — sadece create modunda anlamli.
    //   null/'' → bos baslangic (clone kullanilmaz)
    //   source_period_id → backend POST /periods/{id}/clone?clone_tasks=true
    // Admin onceki donemi secince form alanlari periodToFormState ile
    // doldurulup 'tarih' auto-fill ile one dogru kaydirilir; submit'te
    // clone endpoint'i cagirilir.
    source_period_id: null,
  }));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [tierDeleteTarget, setTierDeleteTarget] = useState(null);
  // İki sekmeli form — uzun scroll yerine temel bilgi / ödül ayrımı.
  const [tab, setTab] = useState('basic');

  // Rozet listesi — rewards sekmesi açıldığında API'den çekiliyor.
  const [badges, setBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [uploadingBadge, setUploadingBadge] = useState(false);
  const [badgeUploadModalOpen, setBadgeUploadModalOpen] = useState(false);
  // Ödül panelleri açık/kapalı — varsayılan hepsi açık.
  const [openPanels, setOpenPanels] = useState({ sks: true, manager: true, bolge: true, genel: true });
  const togglePanel = (key) => setOpenPanels((p) => ({ ...p, [key]: !p[key] }));

  // Form ilk açıldığında referans listelerini yükle (tenant-scoped cache).
  useEffect(() => {
    if (!open) return;
    if (segmentsStatus === 'idle') dispatch(fetchSegments({ include_inactive: false }));
    if (mudurluklerStatus === 'idle') dispatch(fetchMudurlukler({ only_active: true }));
  }, [open, segmentsStatus, mudurluklerStatus, dispatch]);

  // Modal her açıldığında ay bazlı defaults'ları yenile — Mayıs'ta açıldıysa
  // "Mayıs 2026" görsün. Yalnızca ad/tarih alanları eski default'ta veya
  // boşsa güncelle; kullanıcı manuel düzenleme yaptıysa ezilmesin.
  // Edit modunda bu akışı atla; formu editing kaydına göre dolduruyoruz.
  useEffect(() => {
    if (!open || isEdit) return;
    setForm((f) => {
      const nextDefaults = monthDefaults();
      // Ad/tarih alanlarından herhangi biri doluysa ve default değil ise
      // kullanıcı elle düzenlemiş olabilir — dokunma. Boş veya önceki
      // default değerlerinde ise yenile.
      const looksUntouched =
        !f.name ||
        /^(Ocak|Şubat|Mart|Nisan|Mayıs|Haziran|Temmuz|Ağustos|Eylül|Ekim|Kasım|Aralık)\s\d{4}$/.test(
          f.name,
        );
      if (!looksUntouched) return f;
      return { ...f, ...nextDefaults };
    });
  }, [open, isEdit]);

  // Edit modunda formu mevcut dönem kaydıyla doldur. Modal açılış/kapanış
  // veya farklı bir kayda geçiş durumunda yeniden senkronla.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm(periodToFormState(editing));
      setErrors({});
      setTab('basic');
    }
  }, [open, isEdit, editing]);

  // Rewards sekmesi ilk açıldığında rozet listesini yükle.
  useEffect(() => {
    if (tab !== 'rewards' || !open) return;
    if (badges.length > 0) return; // zaten yüklü
    setBadgesLoading(true);
    badgesApi.list()
      .then((r) => setBadges(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBadges([]))
      .finally(() => setBadgesLoading(false));
  }, [tab, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  // ---- Rank bazlı ödül kademeleri (reward_template.tiers) ----
  const tierRows = Array.isArray(form.reward_tiers) ? form.reward_tiers : [];
  const updateTier = (idx, patch) => {
    const next = tierRows.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    setForm((f) => ({ ...f, reward_tiers: next }));
    if (errors.reward_tiers) setErrors((p) => ({ ...p, reward_tiers: undefined }));
  };
  const addTier = () => {
    const lastTo = tierRows.length ? Number(tierRows[tierRows.length - 1].rank_to || 0) : 0;
    const nextFrom = lastTo + 1;
    setForm((f) => ({
      ...f,
      reward_tiers: [
        ...tierRows,
        { rank_from: nextFrom, rank_to: nextFrom, aspara: 0 },
      ],
    }));
  };
  const performRemoveTier = (idx) =>
    setForm((f) => ({
      ...f,
      reward_tiers: tierRows.filter((_, i) => i !== idx),
    }));
  const removeTier = (idx) => {
    // Veri girilmemis bos tier'lari sessizce sil — UX akicilik.
    // Aspara degeri girilmis tier'larda confirm dialog goster.
    const tier = tierRows[idx];
    if (!tier || !Number(tier.aspara)) {
      performRemoveTier(idx);
      return;
    }
    setTierDeleteTarget({ idx, tier });
  };

  // ---- Türkiye geneli tier helpers (genel_tiers) ----
  const genelTierRows = Array.isArray(form.genel_tiers) ? form.genel_tiers : [];
  const updateGenelTier = (idx, patch) => {
    const next = genelTierRows.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    setForm((f) => ({ ...f, genel_tiers: next }));
  };
  const addGenelTier = () => {
    const lastTo = genelTierRows.length ? Number(genelTierRows[genelTierRows.length - 1].rank_to || 0) : 0;
    setForm((f) => ({
      ...f,
      genel_tiers: [
        ...genelTierRows,
        { rank_from: lastTo + 1, rank_to: lastTo + 1, aspara: 0 },
      ],
    }));
  };
  const removeGenelTier = (idx) =>
    setForm((f) => ({
      ...f,
      genel_tiers: genelTierRows.filter((_, i) => i !== idx),
    }));

  // ---- Yönetici kademeleri (manager_tiers) ----
  const mgrTierRows = Array.isArray(form.manager_tiers) ? form.manager_tiers : [];
  const updateMgrTier = (idx, patch) => {
    const next = mgrTierRows.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    setForm((f) => ({ ...f, manager_tiers: next }));
  };
  const addMgrTier = () => {
    const lastTo = mgrTierRows.length ? Number(mgrTierRows[mgrTierRows.length - 1].rank_to || 0) : 0;
    setForm((f) => ({
      ...f,
      manager_tiers: [
        ...mgrTierRows,
        { rank_from: lastTo + 1, rank_to: lastTo + 1, aspara: 0 },
      ],
    }));
  };
  const removeMgrTier = (idx) =>
    setForm((f) => ({
      ...f,
      manager_tiers: mgrTierRows.filter((_, i) => i !== idx),
    }));

  // ---- Rozet ödülleri — bölge içi / türkiye geneli / yönetici ----
  const bolgeBARows   = Array.isArray(form.badge_awards_bolge)   ? form.badge_awards_bolge   : [];
  const genelBARows   = Array.isArray(form.badge_awards_genel)   ? form.badge_awards_genel   : [];
  const managerBARows = Array.isArray(form.badge_awards_manager) ? form.badge_awards_manager : [];

  // Yeni rozet ödülü satırı eklerken son satırın rank_to'sundan sonraki sıraya
  // otomatik konumlandır (son satır 3-3 ise yeni satır 4-4). Boş listede 1-1.
  // Aspara kademeleri (`addTier`) ile aynı UX.
  const _addBA    = (field) => setForm((f) => {
    const rows = f[field] || [];
    const lastTo = rows.length ? Number(rows[rows.length - 1].rank_to || 0) : 0;
    const nextFrom = (Number.isFinite(lastTo) && lastTo > 0 ? lastTo : 0) + 1;
    return {
      ...f,
      [field]: [...rows, { badge_id: '', rank_from: nextFrom, rank_to: nextFrom }],
    };
  });
  const _updateBA = (field, idx, patch) => setForm((f) => ({ ...f, [field]: (f[field] || []).map((a, i) => (i === idx ? { ...a, ...patch } : a)) }));
  const _removeBA = (field, idx) => setForm((f) => ({ ...f, [field]: (f[field] || []).filter((_, i) => i !== idx) }));

  // Hangi bölümden "Rozet Yükle" basıldığını takip et.
  const [badgeUploadContext, setBadgeUploadContext] = useState('badge_awards_bolge');
  const openUpload = (field) => { setBadgeUploadContext(field); setBadgeUploadModalOpen(true); };

  // Rozet yükleme — BadgeUploadModal'dan (file, name) alır, yüklenen rozeti
  // upload'ı tetikleyen bölüme (bolge / genel / manager) otomatik ekler.
  const handleBadgeUpload = async (file, name) => {
    setUploadingBadge(true);
    try {
      const res = await badgesApi.upload(file, name);
      const newBadge = res.data;
      setBadges((prev) => [newBadge, ...prev]);
      setBadgeUploadModalOpen(false);
      dispatch(pushToast({ kind: 'success', message: `"${name}" yüklendi.` }));
      setForm((f) => {
        const field = badgeUploadContext;
        const awards = [...(f[field] || [])];
        const emptyIdx = awards.findIndex((a) => !a.badge_id);
        if (emptyIdx >= 0) {
          awards[emptyIdx] = { ...awards[emptyIdx], badge_id: newBadge.id };
        } else {
          awards.push({ badge_id: newBadge.id, rank_from: 1, rank_to: 1 });
        }
        return { ...f, [field]: awards };
      });
    } catch (err) {
      dispatch(pushToast({
        kind: 'error',
        message: err?.detail || err?.message || 'Rozet yüklenemedi. Dosya tipi veya boyutu uygun olmayabilir.',
      }));
    } finally {
      setUploadingBadge(false);
    }
  };

  /**
   * Flat form state → backend PeriodCreate payload. Hedef kitle ``segment_ids``
   * üzerinden; UI'da "Kullanıcı Tipi (Segment)" etiketi ile gösterilir. Rank bazlı
   * ödüller ``reward_template.tiers`` altında gönderilir (her satır
   * backend ``RewardTier`` şemasına uygun).
   */
  // Tier array (UI) → backend formatina donusum helper.
  const buildTierArray = (rows) =>
    (rows || []).map((t) => {
      const rf = Number(t.rank_from || 0);
      const rt = Number(t.rank_to || rf);
      const label = rf === rt ? `${rf}. Sıra` : `${rf}-${rt}. Sıra`;
      return {
        rank_from: rf,
        rank_to: rt,
        aspara: Number(t.aspara || 0),
        label,
      };
    });

  const buildPayload = () => ({
    name: form.name.trim(),
    start_date: form.start_date,
    end_date: form.end_date,
    description: form.description?.trim() || null,
    reward_template: {
      // v2: bolge_tiers + genel_tiers bağımsız, ikisi aynı anda aktif olabilir.
      // Worker her iki listeden aspara hesaplar ve toplar.
      bolge_tiers: buildTierArray(tierRows),
      genel_tiers: buildTierArray(genelTierRows),
      // per_mudurluk: bölge içi tier override (bolge_tiers'a göre çalışır).
      ...((form.reward_per_mudurluk || []).length > 0
        ? {
            per_mudurluk: (form.reward_per_mudurluk || []).reduce((acc, entry) => {
              if (entry && entry.mudurluk && Array.isArray(entry.tiers)) {
                acc[entry.mudurluk] = buildTierArray(entry.tiers);
              }
              return acc;
            }, {}),
          }
        : {}),
      baraj_puan: Number(form.min_total_points || 0),
      // Yönetici oyunlaştırma — HYO bazlı sıralama kademeleri + baraj.
      manager_tiers: buildTierArray(mgrTierRows),
      manager_baraj_min_sks: Number(form.manager_baraj_min_sks || 1),
      manager_baraj_min_hyo: Number(form.manager_baraj_min_hyo || 0),
      // Dönem rozet ödülleri — bölge içi / türkiye geneli / yönetici.
      badge_awards: {
        bolge: (form.badge_awards_bolge || []).filter((a) => a.badge_id).map((a) => ({
          badge_id: Number(a.badge_id), rank_from: Number(a.rank_from || 1), rank_to: Number(a.rank_to || 1),
        })),
        genel: (form.badge_awards_genel || []).filter((a) => a.badge_id).map((a) => ({
          badge_id: Number(a.badge_id), rank_from: Number(a.rank_from || 1), rank_to: Number(a.rank_to || 1),
        })),
        manager: (form.badge_awards_manager || []).filter((a) => a.badge_id).map((a) => ({
          badge_id: Number(a.badge_id), rank_from: Number(a.rank_from || 1), rank_to: Number(a.rank_to || 1),
        })),
      },
    },
    segment_ids: Array.from(form.segment_ids || []),
    mudurluk_kodlari:
      (form.mudurluk_kodlari || []).length
        ? Array.from(form.mudurluk_kodlari)
        : null,
    max_total_points: form.max_total_points ? Number(form.max_total_points) : null,
    aspara_per_point: Number(form.aspara_per_point || 0),
  });

  // Sekme rozetleri — tab başlıklarının yanında küçük özet (kaç kademe,
  // oran var mı) kullanıcı formun hangi kısmında ne yaptığını görebilsin.
  const BASIC_FIELDS = [
    'name',
    'start_date',
    'end_date',
    'min_total_points',
    'max_total_points',
  ];
  const AUDIENCE_FIELDS = ['segment_ids', 'mudurluk_kodlari'];
  const REWARD_FIELDS = ['aspara_per_point', 'reward_tiers'];
  const basicErrorCount = BASIC_FIELDS.reduce((n, k) => n + (errors[k] ? 1 : 0), 0);
  const audienceErrorCount = AUDIENCE_FIELDS.reduce((n, k) => n + (errors[k] ? 1 : 0), 0);
  const rewardErrorCount = REWARD_FIELDS.reduce((n, k) => n + (errors[k] ? 1 : 0), 0);

  // Form submit'te hata varsa ilgili sekmeye otomatik at — kullanıcı
  // "Kaydet"e basınca sessizce pasif sekmede kalan hatayı kaçırmasın.
  const handleSubmitWithTabJump = async (e) => {
    e.preventDefault();
    const { ok, errors: errs } = validate(periodSchema, form);
    if (!ok) {
      setErrors(errs);
      if (BASIC_FIELDS.some((k) => errs[k])) setTab('basic');
      else if (AUDIENCE_FIELDS.some((k) => errs[k])) setTab('audience');
      else setTab('rewards');
      dispatch(pushToast({ kind: 'error', message: firstError(errs) || 'Form eksik' }));
      return;
    }
    setErrors({});
    setSaving(true);
    const payload = buildPayload();
    // Create akisinda "Baslangic Sablonu" secilmisse clone endpoint'ine
    // git — backend kaynak donemin tum aktif gorevlerini yeni donem
    // altina kopyalar. Edit akisinda bu yol devreye girmez.
    let action;
    if (isEdit) {
      action = updatePeriod({ id: editing.id, payload });
    } else if (form.source_period_id) {
      action = clonePeriod({
        sourcePeriodId: Number(form.source_period_id),
        payload,
        cloneTasks: true,
      });
    } else {
      action = createPeriod(payload);
    }
    const res = await dispatch(action);
    setSaving(false);
    if (res.meta.requestStatus === 'fulfilled') {
      dispatch(
        pushToast({
          kind: 'success',
          message: isEdit
            ? `"${form.name}" güncellendi`
            : `Dönem "${form.name}" oluşturuldu`,
        }),
      );
      onClose();
      setTab('basic');
      if (!isEdit) {
        // Ay defaults'ları yeniden al — ardışık dönem açma akışı için
        // uygun değerlerle hazır olsun. Edit akışında dokunma (next open
        // `editing` veya create boş state'i gelecek).
        setForm({
          ...form,
          ...monthDefaults(),
          description: '',
          // Yeni dönem oluştururken puan ve üst sınır boş başlasın
          // (önceki dönem değerleri taşımasın) — "Boş Başla" semantiği.
          min_total_points: '',
          max_total_points: '',
          segment_ids: [],
          mudurluk_kodlari: [],
          reward_tiers: [],
          genel_tiers: [],
          reward_per_mudurluk: [],
          manager_tiers: [],
          manager_baraj_min_sks: 1,
          manager_baraj_min_hyo: 0,
          badge_awards_bolge: [],
          badge_awards_genel: [],
          badge_awards_manager: [],
        });
      }
    } else {
      dispatch(
        pushToast({
          kind: 'error',
          message:
            res.payload ||
            (isEdit ? 'Dönem güncellenemedi' : 'Dönem oluşturulamadı'),
        }),
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Dönem Düzenle' : 'Yeni Dönem Oluştur'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            İptal
          </Button>
          <Button loading={saving} onClick={handleSubmitWithTabJump}>
            {isEdit ? 'Kaydet' : 'Taslak Olarak Kaydet'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmitWithTabJump} className="space-y-5">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            {
              key: 'basic',
              label: basicErrorCount ? `Temel Bilgiler ⚠` : 'Temel Bilgiler',
            },
            {
              key: 'audience',
              label: audienceErrorCount ? `Hedef Kitle ⚠` : 'Hedef Kitle',
              count:
                (form.segment_ids?.length || 0) +
                  (form.mudurluk_kodlari?.length || 0) || undefined,
            },
            {
              key: 'rewards',
              label: rewardErrorCount ? `Ödüller ⚠` : 'Ödüller',
              count:
                (tierRows.length + genelTierRows.length + mgrTierRows.length +
                  bolgeBARows.filter((a) => a.badge_id).length +
                  genelBARows.filter((a) => a.badge_id).length +
                  managerBARows.filter((a) => a.badge_id).length) || undefined,
            },
          ]}
        />

        {tab === 'basic' && (
          <div className="space-y-5">
            {/* Baslangic sablonu — sadece yeni donem olustururken. Admin
                onceki donemden baslamayi sececegi zaman backend clone
                endpoint'i cagrilir, source donemdeki tum gorevler yeni
                donem altina kopyalanir. Sayfa form alanlari kullanicinin
                girdigi yeni ad/tarih/baraj/odul ile dolu kalir. */}
            {!isEdit && (
              <section className="space-y-3">
                <SectionTitle
                  title="Başlangıç Şablonu"
                  hint="Yeni dönemi sıfırdan kur veya önceki bir dönemin görevlerini kopyala"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label
                    className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                      !form.source_period_id
                        ? 'border-primary-400 bg-primary-50/40'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={!form.source_period_id}
                      onChange={() =>
                        setForm((f) => ({ ...f, source_period_id: null }))
                      }
                      className="mt-0.5"
                    />
                    <div className="text-sm">
                      <p className="font-medium text-neutral-900">Boş Başla</p>
                      <p className="text-xs text-neutral-600 mt-0.5">
                        Görev listesi boş, her şeyi kendin ekleyeceksin.
                      </p>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                      form.source_period_id
                        ? 'border-primary-400 bg-primary-50/40'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={!!form.source_period_id}
                      onChange={() =>
                        setForm((f) => ({
                          ...f,
                          // Varsayilan: en son olusturulmus donem. Liste bos
                          // ise clone modunu acmayalim; boyle bir durumda
                          // kullanici "Bos Basla" zaten aktif.
                          source_period_id:
                            f.source_period_id || (periodOptionsForClone?.[0]?.id ?? ''),
                        }))
                      }
                      className="mt-0.5"
                    />
                    <div className="text-sm">
                      <p className="font-medium text-neutral-900">
                        Önceki Dönemden Kopyala
                      </p>
                      <p className="text-xs text-neutral-600 mt-0.5">
                        Seçtiğin dönemdeki tüm aktif görevler (tiers dahil)
                        yeni döneme kopyalanır.
                      </p>
                    </div>
                  </label>
                </div>
                {form.source_period_id != null && (
                  <Select
                    label="Kaynak Dönem"
                    required
                    value={form.source_period_id || ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        source_period_id: Number(e.target.value) || null,
                      }))
                    }
                    placeholder="Kopyalanacak dönemi seç"
                    options={((periodOptionsForClone) || []).map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))}
                  />
                )}
              </section>
            )}

            {/* Bölüm: Dönem kimliği */}
            <section className="space-y-3">
              <SectionTitle title="Dönem Kimliği" hint="Ad, tarih aralığı ve baraj puanları" />
              <Input
                label="Dönem Adı"
                required
                value={form.name}
                onChange={set('name')}
                placeholder="Nisan 2026"
                error={errors.name}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Başlangıç"
                  type="date"
                  required
                  value={form.start_date}
                  onChange={set('start_date')}
                  error={errors.start_date}
                />
                <Input
                  label="Bitiş"
                  type="date"
                  required
                  value={form.end_date}
                  onChange={set('end_date')}
                  error={errors.end_date}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Min. Toplam Puan"
                  type="number"
                  value={form.min_total_points}
                  onChange={set('min_total_points')}
                  hint="Baraj puanı — altında kalan oyuncular ödül almaz"
                  error={errors.min_total_points}
                />
                <Input
                  label="Max. Toplam Puan"
                  type="number"
                  value={form.max_total_points}
                  onChange={set('max_total_points')}
                  hint="Üst sınır (opsiyonel)"
                  error={errors.max_total_points}
                />
              </div>
            </section>

            {/* Bölüm: Açıklama */}
            <section className="space-y-3">
              <SectionTitle title="Açıklama" hint="Opsiyonel" />
              <Textarea
                value={form.description}
                onChange={set('description')}
                rows={3}
                placeholder="Bu dönemin amacı, kapsamı, notlar…"
              />
            </section>
          </div>
        )}

        {tab === 'audience' && (
          <div className="space-y-5">
            {/* Bölüm: Hedef kitle */}
            <section className="space-y-3">
              <SectionTitle
                title="Hedef Kitle"
                hint="Dönemin kimlere uygulanacağını kısıtlayın (opsiyonel)"
              />
              <MultiSelectChips
                label="Kullanıcı Tipi (Segment)"
                loading={segmentsStatus === 'loading'}
                options={segments.map((s) => ({
                  value: s.name || String(s.id),
                  label: s.name || `#${s.id}`,
                  hint: s.is_active === false ? '(pasif)' : undefined,
                }))}
                value={form.segment_ids}
                onChange={(next) => {
                  setForm({ ...form, segment_ids: next });
                  if (errors.segment_ids) setErrors((p) => ({ ...p, segment_ids: undefined }));
                }}
                hint="Bu oyunlaştırma dönemini belirli bir kullanıcı tipi için özelleştirebilirsiniz. Boş bırakırsanız dönem tüm kullanıcılara uygulanır."
                emptyLabel="Tenant için tanımlı kullanıcı tipi bulunamadı"
                error={errors.segment_ids}
              />
              <MultiSelectChips
                label="Müdürlükler"
                loading={mudurluklerStatus === 'loading'}
                options={mudurlukler.map((m) => ({
                  value: m.code,
                  label: m.code,
                  hint: m.user_count ? `${m.user_count} kullanıcı` : undefined,
                }))}
                value={form.mudurluk_kodlari}
                onChange={(next) => {
                  setForm({ ...form, mudurluk_kodlari: next });
                  if (errors.mudurluk_kodlari)
                    setErrors((p) => ({ ...p, mudurluk_kodlari: undefined }));
                }}
                hint="Boş bırakırsan tüm müdürlükler dahil edilir"
                emptyLabel="Tenant için tanımlı müdürlük bulunamadı"
                error={errors.mudurluk_kodlari}
              />
            </section>
          </div>
        )}

        {tab === 'rewards' && (
          <div className="space-y-3">

            {/* ═══════════════════════════════════════════
                Panel 1 — Dönem Ödülleri (mavi)
            ════════════════════════════════════════════ */}
            <div className="rounded-xl border border-sky-200 overflow-hidden">
              {/* Panel başlığı — tıklanabilir, açar/kapar */}
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-sky-50 hover:bg-sky-100/70 transition-colors text-left"
                onClick={() => togglePanel('sks')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 text-white">
                    <Icon name="trophy" className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-sky-900 leading-tight">Dönem Ödülleri</p>
                    <p className="text-[11px] text-sky-600 mt-0.5">
                      Puan dönüşümü · Grup içi sıralama · Genel Sıralama · Müdürlük özel
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {tierRows.length > 0 && (
                    <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                      {tierRows.length} grup içi kad.
                    </span>
                  )}
                  {genelTierRows.length > 0 && (
                    <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                      {genelTierRows.length} genel kad.
                    </span>
                  )}
                  {(bolgeBARows.filter((a) => a.badge_id).length + genelBARows.filter((a) => a.badge_id).length) > 0 && (
                    <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                      {bolgeBARows.filter((a) => a.badge_id).length + genelBARows.filter((a) => a.badge_id).length} rozet
                    </span>
                  )}
                  <Icon
                    name={openPanels.sks ? 'chevron-up' : 'chevron-down'}
                    className="h-4 w-4 text-sky-500"
                  />
                </div>
              </button>

              {openPanels.sks && (
                <div className="border-t border-sky-200 bg-white divide-y divide-neutral-100">

                  {/* ── Puan → ASPara ── */}
                  <div className="px-4 py-4 space-y-2">
                    <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                      Puan → ASPara Dönüşümü
                    </p>
                    <Input
                      label="1 Puan = ? ASPara"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.aspara_per_point}
                      onChange={set('aspara_per_point')}
                      hint="0 yazarsanız puan başına ASPara verilmez; yalnızca aşağıdaki rank ödülleri geçerli olur."
                      error={errors.aspara_per_point}
                    />
                  </div>

                  {/* ══ Grup İçi Sub-Panel (emerald) ══ */}
                  <div className="border-t border-emerald-100 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100/60 transition-colors text-left"
                      onClick={() => togglePanel('bolge')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                          <Icon name="map-pin" className="h-2.5 w-2.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-emerald-900 leading-tight">Grup İçi Sıralama</p>
                          <p className="text-[10px] text-emerald-600 mt-0.5">
                            Her müdürlük kendi içinde sıralanır — İstanbul 1.'si ile Kıbrıs 1.'si ayrı kazanır
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {tierRows.length > 0 && (
                          <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                            {tierRows.length} kademe
                          </span>
                        )}
                        {bolgeBARows.filter((a) => a.badge_id).length > 0 && (
                          <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                            {bolgeBARows.filter((a) => a.badge_id).length} rozet
                          </span>
                        )}
                        <Icon
                          name={openPanels.bolge ? 'chevron-up' : 'chevron-down'}
                          className="h-3.5 w-3.5 text-emerald-500"
                        />
                      </div>
                    </button>

                    {openPanels.bolge && (
                      <div className="divide-y divide-emerald-50">
                        {/* Grup İçi Rank */}
                        <div className="px-4 py-4 space-y-2 bg-white">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                              ASPara Kademeleri
                            </p>
                            <Button size="sm" variant="secondary" icon="plus" onClick={addTier}>
                              Kademe Ekle
                            </Button>
                          </div>
                          {tierRows.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-emerald-100 bg-emerald-50/40 p-3 text-center">
                              <p className="text-xs text-emerald-600/60">Grup içi rank ödülü tanımlanmadı.</p>
                            </div>
                          ) : (
                            <TierTable rows={tierRows} onUpdate={updateTier} onRemove={removeTier} />
                          )}
                          {errors.reward_tiers && (
                            <p className="text-xs text-red-600">{errors.reward_tiers}</p>
                          )}
                        </div>
                        {/* Grup İçi Rozet */}
                        <div className="px-4 py-3 space-y-2 bg-emerald-50/30">
                          <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                            Rozet Ödülleri
                            <span className="ml-1.5 normal-case font-normal text-emerald-500">(opsiyonel)</span>
                          </p>
                          <BadgeAwardSection
                            rows={bolgeBARows}
                            badges={badges}
                            badgesLoading={badgesLoading}
                            onUpdate={(i, p) => _updateBA('badge_awards_bolge', i, p)}
                            onRemove={(i) => _removeBA('badge_awards_bolge', i)}
                            onAdd={() => _addBA('badge_awards_bolge')}
                            onUpload={() => openUpload('badge_awards_bolge')}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ══ Türkiye Geneli Sub-Panel (indigo) ══ */}
                  <div className="border-t border-indigo-100 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100/60 transition-colors text-left"
                      onClick={() => togglePanel('genel')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white">
                          <Icon name="globe" className="h-2.5 w-2.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-indigo-900 leading-tight">Genel Sıralama</p>
                          <p className="text-[10px] text-indigo-600 mt-0.5">
                            Tüm müdürlükler tek sıralamada yarışır · Müdürlük ödülleriyle birlikte uygulanır
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {genelTierRows.length > 0 && (
                          <span className="rounded-full bg-indigo-200 px-2 py-0.5 text-[10px] font-medium text-indigo-800">
                            {genelTierRows.length} kademe
                          </span>
                        )}
                        {genelBARows.filter((a) => a.badge_id).length > 0 && (
                          <span className="rounded-full bg-indigo-200 px-2 py-0.5 text-[10px] font-medium text-indigo-800">
                            {genelBARows.filter((a) => a.badge_id).length} rozet
                          </span>
                        )}
                        <Icon
                          name={openPanels.genel ? 'chevron-up' : 'chevron-down'}
                          className="h-3.5 w-3.5 text-indigo-500"
                        />
                      </div>
                    </button>

                    {openPanels.genel && (
                      <div className="divide-y divide-indigo-50">
                        {/* Türkiye Geneli Rank */}
                        <div className="px-4 py-4 space-y-2 bg-white">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                              ASPara Kademeleri
                            </p>
                            <Button size="sm" variant="secondary" icon="plus" onClick={addGenelTier}>
                              Kademe Ekle
                            </Button>
                          </div>
                          {genelTierRows.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-indigo-100 bg-indigo-50/40 p-3 text-center">
                              <p className="text-xs text-indigo-600/60">Genel Sıralama rank ödülü tanımlanmadı.</p>
                            </div>
                          ) : (
                            <TierTable rows={genelTierRows} onUpdate={updateGenelTier} onRemove={removeGenelTier} />
                          )}
                        </div>
                        {/* Türkiye Geneli Rozet */}
                        <div className="px-4 py-3 space-y-2 bg-indigo-50/30">
                          <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                            Rozet Ödülleri
                            <span className="ml-1.5 normal-case font-normal text-indigo-500">(opsiyonel)</span>
                          </p>
                          <BadgeAwardSection
                            rows={genelBARows}
                            badges={badges}
                            badgesLoading={badgesLoading}
                            onUpdate={(i, p) => _updateBA('badge_awards_genel', i, p)}
                            onRemove={(i) => _removeBA('badge_awards_genel', i)}
                            onAdd={() => _addBA('badge_awards_genel')}
                            onUpload={() => openUpload('badge_awards_genel')}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Müdürlük Özel Override ── */}
                  <div className="px-4 py-4 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                          Müdürlük Özel Ödüller
                          <span className="ml-1.5 normal-case font-normal text-neutral-400">(opsiyonel)</span>
                        </p>
                        <p className="text-[11px] text-neutral-400 mt-0.5">
                          Belirli müdürlükler için farklı tutar — tanımsız müdürlükler yukarıdaki tabloyu kullanır
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        icon="plus"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            reward_per_mudurluk: [
                              ...(f.reward_per_mudurluk || []),
                              { mudurluk: '', tiers: [] },
                            ],
                          }))
                        }
                      >
                        Müdürlük Ekle
                      </Button>
                    </div>
                    {(form.reward_per_mudurluk || []).length === 0 ? (
                      <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-3 text-center">
                        <p className="text-xs text-neutral-400">Müdürlük-özel ödül yok. Tüm müdürlükler varsayılan tabloyu kullanır.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(form.reward_per_mudurluk || []).map((entry, blockIdx) => (
                          <div
                            key={blockIdx}
                            className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2"
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <div className="flex-1">
                                <Select
                                  label="Müdürlük"
                                  value={entry.mudurluk || ''}
                                  onChange={(e) =>
                                    setForm((f) => {
                                      const list = [...(f.reward_per_mudurluk || [])];
                                      list[blockIdx] = { ...list[blockIdx], mudurluk: e.target.value };
                                      return { ...f, reward_per_mudurluk: list };
                                    })
                                  }
                                  options={[
                                    { value: '', label: '— Müdürlük seçin —' },
                                    ...(mudurlukler || []).map((m) => ({
                                      value: m.code || m.kod || m.value || String(m),
                                      label: `${m.code || m.ad || m.label || String(m)}${m.user_count ? ` (${m.user_count} kullanıcı)` : ''}`,
                                    })),
                                  ]}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((f) => ({
                                    ...f,
                                    reward_per_mudurluk: (f.reward_per_mudurluk || []).filter(
                                      (_, i) => i !== blockIdx,
                                    ),
                                  }))
                                }
                                title="Müdürlük özel ödülü kaldır"
                                className="self-start mt-6 rounded-md p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                              >
                                <Icon name="trash" className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="space-y-1.5">
                              {(entry.tiers || []).map((t, i) => (
                                <div
                                  key={i}
                                  className="grid grid-cols-[0.9fr_0.9fr_1.4fr_auto] items-center gap-3 bg-white rounded px-3 py-2 border border-neutral-200"
                                >
                                  <Input
                                    type="number" min={1} value={t.rank_from ?? ''} placeholder="Başl."
                                    onChange={(e) =>
                                      setForm((f) => {
                                        const list = [...(f.reward_per_mudurluk || [])];
                                        const tiers = [...(list[blockIdx].tiers || [])];
                                        tiers[i] = { ...tiers[i], rank_from: Number(e.target.value) };
                                        list[blockIdx] = { ...list[blockIdx], tiers };
                                        return { ...f, reward_per_mudurluk: list };
                                      })
                                    }
                                  />
                                  <Input
                                    type="number" min={1} value={t.rank_to ?? ''} placeholder="Bit."
                                    onChange={(e) =>
                                      setForm((f) => {
                                        const list = [...(f.reward_per_mudurluk || [])];
                                        const tiers = [...(list[blockIdx].tiers || [])];
                                        tiers[i] = { ...tiers[i], rank_to: Number(e.target.value) };
                                        list[blockIdx] = { ...list[blockIdx], tiers };
                                        return { ...f, reward_per_mudurluk: list };
                                      })
                                    }
                                  />
                                  <Input
                                    type="number" min={0} step="1" value={t.aspara ?? ''} placeholder="Aspara"
                                    onChange={(e) =>
                                      setForm((f) => {
                                        const list = [...(f.reward_per_mudurluk || [])];
                                        const tiers = [...(list[blockIdx].tiers || [])];
                                        tiers[i] = { ...tiers[i], aspara: Number(e.target.value) };
                                        list[blockIdx] = { ...list[blockIdx], tiers };
                                        return { ...f, reward_per_mudurluk: list };
                                      })
                                    }
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setForm((f) => {
                                        const list = [...(f.reward_per_mudurluk || [])];
                                        const tiers = (list[blockIdx].tiers || []).filter((_, x) => x !== i);
                                        list[blockIdx] = { ...list[blockIdx], tiers };
                                        return { ...f, reward_per_mudurluk: list };
                                      })
                                    }
                                    className="rounded-md p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                                    title="Ödülü sil"
                                  >
                                    <Icon name="trash" className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              <Button
                                size="sm" variant="secondary" icon="plus"
                                onClick={() =>
                                  setForm((f) => {
                                    const list = [...(f.reward_per_mudurluk || [])];
                                    const tiers = [...(list[blockIdx].tiers || [])];
                                    const lastTo = tiers.length ? Number(tiers[tiers.length - 1].rank_to || 0) : 0;
                                    tiers.push({ rank_from: lastTo + 1, rank_to: lastTo + 1, aspara: 0 });
                                    list[blockIdx] = { ...list[blockIdx], tiers };
                                    return { ...f, reward_per_mudurluk: list };
                                  })
                                }
                              >
                                Ödül Ekle
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════
                Panel 2 — Yönetici Ödülleri (violet)
            ════════════════════════════════════════════ */}
            <div className="rounded-xl border border-violet-200 overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-violet-50 hover:bg-violet-100/70 transition-colors text-left"
                onClick={() => togglePanel('manager')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-violet-500 text-white">
                    <Icon name="users" className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-violet-900 leading-tight">Yönetici Ödülleri</p>
                    <p className="text-[11px] text-violet-600 mt-0.5">
                      HYO sıralaması · SKS sayısı + HYO barajı · SKS ödülleriyle bağımsız
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {mgrTierRows.length > 0 && (
                    <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                      {mgrTierRows.length} kademe
                    </span>
                  )}
                  {managerBARows.filter((a) => a.badge_id).length > 0 && (
                    <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                      {managerBARows.filter((a) => a.badge_id).length} rozet
                    </span>
                  )}
                  {form.manager_baraj_min_hyo > 0 && (
                    <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                      HYO ≥ %{form.manager_baraj_min_hyo}
                    </span>
                  )}
                  <Icon
                    name={openPanels.manager ? 'chevron-up' : 'chevron-down'}
                    className="h-4 w-4 text-violet-500"
                  />
                </div>
              </button>

              {openPanels.manager && (
                <div className="border-t border-violet-200 bg-white divide-y divide-neutral-100">

                  {/* ── Baraj koşulları ── */}
                  <div className="px-4 py-4 space-y-3">
                    <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                      Ödül Barajı
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Min. SKS Sayısı"
                        type="number"
                        min={1}
                        value={form.manager_baraj_min_sks}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, manager_baraj_min_sks: Number(e.target.value) || 1 }))
                        }
                        hint="Sıralamaya girmesi gereken min. SKS (varsayılan: 1)"
                      />
                      <Input
                        label="Min. HYO % (Opsiyonel)"
                        type="number"
                        min={0}
                        max={100}
                        step="1"
                        value={form.manager_baraj_min_hyo || ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, manager_baraj_min_hyo: Number(e.target.value) || 0 }))
                        }
                        hint="HYO bu değerin altındaysa ödül alamaz · 0 = devre dışı"
                        placeholder="Örn. 50"
                      />
                    </div>
                    <p className="text-[11px] text-neutral-400 leading-relaxed">
                      İki koşul <strong>OR</strong> ile değil <strong>AND</strong> ile çalışır — iki barajı da geçemeyen yönetici ödül alamaz.
                      HYO = Gerçekleşen / Hedef × 100.
                    </p>
                  </div>

                  {/* ── Yönetici kademeleri ── */}
                  <div className="px-4 py-4 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                        ASPara Kademeleri
                      </p>
                      <Button size="sm" variant="secondary" icon="plus" onClick={addMgrTier}>
                        Kademe Ekle
                      </Button>
                    </div>
                    {mgrTierRows.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-3 text-center">
                        <p className="text-xs text-neutral-400">
                          Yönetici rank ödülü tanımlanmadı. Yöneticiler yalnızca puan bazlı ASPara kazanır.
                        </p>
                      </div>
                    ) : (
                      <TierTable rows={mgrTierRows} onUpdate={updateMgrTier} onRemove={removeMgrTier} />
                    )}
                  </div>

                  {/* ── Yönetici Rozet Ödülleri ── */}
                  <div className="px-4 py-3 space-y-2 bg-violet-50/40">
                    <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
                      Yönetici Rozet Ödülleri
                      <span className="ml-1.5 normal-case font-normal text-neutral-400">(opsiyonel)</span>
                    </p>
                    <BadgeAwardSection
                      rows={managerBARows}
                      badges={badges}
                      badgesLoading={badgesLoading}
                      onUpdate={(i, p) => _updateBA('badge_awards_manager', i, p)}
                      onRemove={(i) => _removeBA('badge_awards_manager', i)}
                      onAdd={() => _addBA('badge_awards_manager')}
                      onUpload={() => openUpload('badge_awards_manager')}
                    />
                  </div>

                </div>
              )}
            </div>

          </div>
        )}
      </form>

      <DeleteModal
        open={!!tierDeleteTarget}
        title="Ödül kademesini silmek istediğinize emin misiniz?"
        description={
          tierDeleteTarget
            ? `Sıra ${tierDeleteTarget.tier.rank_from}–${tierDeleteTarget.tier.rank_to} için tanımlı ${Number(tierDeleteTarget.tier.aspara).toLocaleString('tr-TR')} Aspara kaldırılacak.`
            : 'Bu işlem geri alınamaz.'
        }
        onClose={() => setTierDeleteTarget(null)}
        onConfirm={() => {
          if (!tierDeleteTarget) return;
          performRemoveTier(tierDeleteTarget.idx);
        }}
      />

      {/* Rozet yükleme modalı */}
      <BadgeUploadModal
        open={badgeUploadModalOpen}
        uploading={uploadingBadge}
        onConfirm={handleBadgeUpload}
        onCancel={() => !uploadingBadge && setBadgeUploadModalOpen(false)}
        onFileError={(msg) => dispatch(pushToast({ kind: 'error', message: msg }))}
      />
    </Modal>
  );
}

/**
 * Rozet küçük resmi — backend image endpoint'i Bearer token gerektirdiğinden
 * <img src> doğrudan çalışmaz. Axios ile blob olarak çekip objectURL yaratıyoruz.
 */
function BadgeThumb({ badgeId, size = 32 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!badgeId) return;
    let objectUrl;
    apiClient
      .get(`/rule-engine/badges/${badgeId}/image`, { responseType: 'blob' })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [badgeId]);

  if (!src) {
    return (
      <div
        style={{ width: size, height: size }}
        className="flex-shrink-0 rounded bg-neutral-100 flex items-center justify-center"
      >
        <Icon name="image" className="h-3 w-3 text-neutral-300" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: size, height: size }}
      className="flex-shrink-0 rounded object-contain border border-neutral-200"
    />
  );
}

/**
 * Rozet ödülü satır listesi — Bölge içi / Türkiye geneli / Yönetici panellerinde
 * tekrarlanan rozet seçim UI'ını tek bileşende toplar.
 * Props:
 *   rows         — badge award dizisi [{badge_id, rank_from, rank_to}]
 *   badges       — yüklü rozet listesi [{id, name}]
 *   badgesLoading— rozetler yükleniyorsa true
 *   onUpdate(i, patch) — satır güncelleme
 *   onRemove(i)        — satır silme
 *   onAdd()            — yeni boş satır ekle
 *   onUpload()         — rozet yükleme modalını aç
 */
function BadgeAwardSection({ rows, badges, badgesLoading, onUpdate, onRemove, onAdd, onUpload }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-neutral-400">
          Her rozet bir <strong>sıra aralığına</strong> atanır (örn: 1–1 = birinci, 1–3 = ilk üç)
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
          >
            <Icon name="upload" className="h-3 w-3" /> Yükle
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
          >
            <Icon name="plus" className="h-3 w-3" /> Ekle
          </button>
        </div>
      </div>
      {badgesLoading ? (
        <p className="text-xs text-neutral-400">Rozetler yükleniyor…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-2 text-center">
          <p className="text-xs text-neutral-400">Rozet ödülü tanımlanmadı.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[2fr_0.7fr_0.7fr_auto] gap-2 px-1">
            <p className="text-[10px] font-semibold uppercase text-neutral-400">Rozet</p>
            <p className="text-[10px] font-semibold uppercase text-neutral-400">Başl.</p>
            <p className="text-[10px] font-semibold uppercase text-neutral-400">Bit.</p>
            <span />
          </div>
          {rows.map((award, i) => (
            <div
              key={i}
              className="grid grid-cols-[2fr_0.7fr_0.7fr_auto] items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {award.badge_id ? (
                  <BadgeThumb badgeId={Number(award.badge_id)} size={24} />
                ) : (
                  <div className="h-6 w-6 flex-shrink-0 rounded bg-neutral-100 flex items-center justify-center">
                    <Icon name="image" className="h-3 w-3 text-neutral-300" />
                  </div>
                )}
                <select
                  className="flex-1 min-w-0 rounded border border-neutral-300 bg-white px-1.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400"
                  value={award.badge_id || ''}
                  onChange={(e) => onUpdate(i, { badge_id: Number(e.target.value) || '' })}
                >
                  <option value="">— Seçin —</option>
                  {badges.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <Input
                type="number" min={1}
                value={award.rank_from ?? ''}
                placeholder="1"
                onChange={(e) => onUpdate(i, { rank_from: Number(e.target.value) })}
              />
              <Input
                type="number" min={1}
                value={award.rank_to ?? ''}
                placeholder="1"
                onChange={(e) => onUpdate(i, { rank_to: Number(e.target.value) })}
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="rounded-md p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
              >
                <Icon name="trash" className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Tier satır tablosu — tekrarlanan rank/aspara tablosunu tek bileşende toplar.
 * onUpdate(idx, patch) ve onRemove(idx) callback'leri dışarıdan gelir.
 */
function TierTable({ rows, onUpdate, onRemove }) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <div className="grid grid-cols-[0.9fr_0.9fr_1.4fr_auto] gap-3 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
        <span>Sıra (Başl.)</span>
        <span>Sıra (Bit.)</span>
        <span>ASPara</span>
        <span className="w-8" />
      </div>
      <div className="divide-y divide-neutral-100">
        {rows.map((t, i) => (
          <div
            key={i}
            className="grid grid-cols-[0.9fr_0.9fr_1.4fr_auto] items-center gap-3 px-3 py-2"
          >
            <Input
              type="number"
              min={1}
              value={t.rank_from ?? ''}
              onChange={(e) => onUpdate(i, { rank_from: Number(e.target.value) })}
            />
            <Input
              type="number"
              min={1}
              value={t.rank_to ?? ''}
              onChange={(e) => onUpdate(i, { rank_to: Number(e.target.value) })}
            />
            <Input
              type="number"
              min={0}
              step="1"
              value={t.aspara ?? ''}
              onChange={(e) => onUpdate(i, { aspara: Number(e.target.value) })}
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="rounded-md p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
              title="Kademeyi sil"
            >
              <Icon name="trash" className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Küçük başlık + hint — form içi bölümlerin görsel olarak gruplanması için.
 * Mevcut diğer admin panellerindeki "Card header" stiline yakın.
 */
function SectionTitle({ title, hint }) {
  return (
    <div className="border-b border-neutral-100 pb-1">
      <h4 className="text-sm font-semibold text-neutral-800">{title}</h4>
      {hint && <p className="text-[11px] text-neutral-500">{hint}</p>}
    </div>
  );
}

export default function Periods() {
  return (
    <Routes>
      <Route index element={<PeriodsList />} />
      <Route path=":id" element={<PeriodDetail />} />
    </Routes>
  );
}
